<template>
  <Card class="care-item">
    <div class="head" @click="toggleForm" :class="{'is-pending': care.isPending}">
      <div class="avatar-wrap">
        <CareIcon :careType="care.careType" />
        <i class="fas fa-clock icon-has-end" v-if="care.isNew && care.careType.hasEndForm"></i>
      </div>
      <p class="care-type">
        <span>{{ care.careType.label }}</span>
        <span class="pending-time" v-if="care.isPending">（進行中 {{ formattedTime }}経過）</span>
      </p>
      <p class="cared-at" v-if="!isNew">
        <i class="fas fa-clock"></i>
        {{care.caredAt.format('HH:mm')}}
      </p>
      <p class="trigger" :class="{'is-open': isFormOpen}">
        <i class="fas fa-angle-down"></i>
      </p>
    </div>
    <Collapse :isOpen="isFormOpen" v-if="formCare">
      <div class="body">
        <div class="body-inner">
          <CareForm
            :isLoading="isLoading"
            :isNew="isNew"
            :isPending="care.isPending"
            :submitLabel="submitLabel"
            @copy="copy"
            @remove="remove"
            @submit="submit"
            v-model="formCare"
            />
        </div>
      </div>
    </Collapse>
  </Card>
</template>

<script>
import Card from '@/components/atoms/Card'
import CareForm from '@/components/molecules/CareForm'
import CareIcon from '@/components/atoms/CareIcon'
import Collapse from '@/components/atoms/Collapse'
import FieldSet from '@/components/molecules/FieldSet'
import TextInput from '@/components/atoms/TextInput'
import UiButton from '@/components/atoms/UiButton'
import moment from 'moment'

import FirebaseCares from '@/firebase/cares.ts'
const firebaseCares = new FirebaseCares()

export default {
  name: 'CareItem',
  data() {
    return {
      isFormOpen: false,
      isLoading: false,
      formCare: null,
      timerSec: 0,
      timer: null,
    }
  },
  computed: {
    isNew() {
      return this.care.isNew
    },
    submitLabel() {
      if (!this.isNew) return '変更'
      if (!this.care.careType.hasEndForm) return '記録'
      return this.care.isPending ? '終了' : '開始'
    },
    formattedTime() {
      return `${~~(this.timerSec / 60)}分${this.timerSec % 60}秒`
    },
  },
  props: {
    care: {
      type: Object,
      default: () => ({}),
    },
    child: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    Card,
    CareForm,
    CareIcon,
    Collapse,
    FieldSet,
    TextInput,
    UiButton,
  },
  watch: {
    care() {
      this.countTimer()
    },
  },
  methods: {
    toggleForm() {
      this.isFormOpen = !this.isFormOpen
      if (this.isFormOpen) {
        this.formCare = {...this.care}
      }
    },
    async remove() {
      if (!window.confirm('削除しますか？')) return
      this.isLoading = true
      try {
        await firebaseCares.removeCare(this.child.id, this.care.id)
        this.$notify('削除しました')
      } catch(error) {
        console.error(error)
        this.$notify('失敗しました')
      }
      this.isLoading = false
    },
    async copy() {
      try {
        this.isLoading = true
        const query = {limit: 1, careType: this.care.careType.key}
        const care = await firebaseCares.getCares(this.child.id, query)
        if (care.length) {
          this.formCare = care[0]
          this.$notify('コピーしました')
        } else {
          this.$notify('前回の記録が見つかりませんでした')
        }
      } catch(error) {
        console.error(error)
        this.$notify('失敗しました')
      }
      this.isLoading = false
    },
    async submit() {
      this.isLoading = true
      try {
        if (this.isNew) {
          if (this.care.careType.hasEndForm) {
            if (this.care.isPending) {
              let care = {...this.formCare, isPending: false}
              if (care.careType.endFn) {
                care = care.careType.endFn(care)
              }
              await firebaseCares.updateCare(this.child.id, care)
              this.$notify('終了を記録しました')
            } else {
              const care = {...this.formCare, isPending: true}
              await firebaseCares.createCare(this.child.id, care)
              this.$notify('開始を記録しました')
            }
          } else {
            await firebaseCares.createCare(this.child.id, this.formCare)
            this.$notify('記録しました')
          }
        } else {
          await firebaseCares.updateCare(this.child.id, this.formCare)
          this.$notify('変更しました')
        }
        this.isFormOpen = false
      } catch(error) {
        console.error(error)
        this.$notify('失敗しました')
      }
      this.isLoading = false
    },
    countTimer() {
      if (this.timer) clearTimeout(this.timer)
      if (this.care.isNew && this.care.isPending) {
        this.timerSec = moment().diff(moment(this.care.createdAt), 'seconds')
        this.timer = setTimeout(() => this.countTimer(), 999)
      }
    },
  },
  mounted() {
    this.formCare = {...this.care}
  },
}
</script>

<style scoped lang="scss">
.head {
  display: flex;
  align-items: center;
  padding: 7px 10px 7px 7px;
  cursor: pointer;


  @keyframes HeadBlink {
    0% {
      background: lighten($attention, 50);
    }

    100% {
      background: lighten($attention, 40);
    }
  }
  &.is-pending {
    animation: HeadBlink 0.5s alternate infinite;
  }
}

.avatar-wrap {
  width: 40px;
  margin-right: 9px;
  position: relative;
}

.icon-has-end {
  position: absolute;
  right: -1px;
  top: 0px;
  font-size: 12px;
  margin-left: 0.25em;
  color: #63b1ba;
}

.care-type {
  font-size: 12px;
  font-weight: bold;
  flex: 1;
}

.cared-at {
  font-size: 10px;
  margin-right: 10px;

  i {
    font-size: 10px;
    color: $natural;
  }
}

.pending-time {
  font-size: 11px;
  font-weight: normal;
}

.trigger {
  i {
    transition: transform 0.6s ease;
  }

  &.is-open {
    i {
      transform: rotate(180deg);
    }
  }
}

.body {
  padding: 0 5px 5px;
}

.body-inner {
  padding: 20px 20px 20px;
  border-radius: 0 0 $round $round;
  background: $background;
}

</style>
